import { useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileText, Upload, Eye, AlertCircle } from "lucide-react";

const SYM = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };

export default function BulkUploader({ onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null); // dry-run result
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const run = async (file, dryRun) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/partner-portal/participants/upload${dryRun ? "?dry_run=true" : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (dryRun) {
        setPreview(data);
        setPendingFile(file);
      } else {
        setResult(data);
        setPendingFile(null); setPreview(null);
        toast.success(`${data.created} participants uploaded · ${data.priced} priced automatically${data.booking_ids?.length ? ` · view #${data.booking_ids.length} in bookings` : ""}`);
        if ((data.errors || []).length > 0) setErrorsOpen(true);
        onUploaded?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (ref.current && !dryRun) ref.current.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/partner-portal/participants/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "aii-partner-participants-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error("Could not download template"); }
  };

  return (
    <div className="aii-card p-4 border-l-4 border-brand" data-testid="bulk-upload">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold flex items-center gap-1.5"><Upload size={14} className="text-brand"/> Bulk upload participants</div>
          <p className="text-xs text-[color:var(--aii-muted)] mt-1">Upload an Excel (.xlsx) or CSV. We accept flexible column names — <b>customer name / participant / name</b>, <b>programme / program_name</b>, <b>duration / duration_weeks</b>, <b>start date / start_date</b>, plus <b>dietary requirements</b>, <b>medical conditions</b>, etc. Fill <b>programme</b> to match one of your Program Catalog entries so pricing auto-fills for the given <b>duration</b>. Hit <b>Preview</b> first to eyeball every row before committing.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="bulk-template" className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><FileText size={12}/> Download template</button>
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid="bulk-upload-btn" className="text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1 disabled:opacity-50"><Eye size={12}/> {busy ? "Reading…" : "Preview + upload"}</button>
          <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => run(e.target.files?.[0], true)}/>
        </div>
      </div>
      {result && (
        <div className="mt-3 text-xs bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] rounded-md p-2 flex flex-wrap gap-3 items-center" data-testid="bulk-result">
          <span><b>{result.created}</b> created</span>
          <span className="text-emerald-800"><b>{result.priced}</b> auto-priced</span>
          {result.unpriced > 0 && <span className="text-amber-800"><b>{result.unpriced}</b> unpriced (no matching rule)</span>}
          {result.warnings?.length > 0 && (
            <span className="text-amber-800 flex items-center gap-1" data-testid="bulk-warnings-inline">
              <AlertCircle size={11}/> {result.warnings.length} unpriced (no catalog match)
            </span>
          )}
          {result.errors?.length > 0 && (
            <button data-testid="bulk-errors-open" onClick={() => setErrorsOpen(true)} className="text-red-800 font-semibold hover:underline flex items-center gap-1">
              <AlertCircle size={11}/> {result.errors.length} error{result.errors.length === 1 ? "" : "s"} · view
            </button>
          )}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="bulk-preview-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)]">
              <h3 className="font-display text-lg font-semibold">Preview · dry run</h3>
              <p className="text-xs text-[color:var(--aii-muted)]">Nothing has been saved. Review the rows below then hit <b>Apply upload</b> to commit — or Cancel and fix your file.</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="bulk-preview-summary">
                <span className="px-2 py-1 rounded bg-sky-100 text-sky-800 font-semibold">{preview.created} rows</span>
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold">{preview.priced} priced</span>
                {preview.unpriced > 0 && <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">{preview.unpriced} unpriced</span>}
                {preview.warnings?.length > 0 && <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">{preview.warnings.length} unpriced</span>}
                {preview.errors?.length > 0 && <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">{preview.errors.length} errors</span>}
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {(preview.previews || []).length === 0 ? (
                <div className="p-6 text-center text-[color:var(--aii-muted)] italic">No valid rows to preview.</div>
              ) : (
                <table className="w-full text-xs" data-testid="bulk-preview-table">
                  <thead className="bg-[color:var(--aii-bg)] sticky top-0"><tr className="text-left">
                    <th className="px-2 py-2">Row</th><th className="px-2 py-2">Customer</th><th className="px-2 py-2">Program</th><th className="px-2 py-2">Location</th><th className="px-2 py-2">Weeks</th><th className="px-2 py-2 text-right">Price</th><th className="px-2 py-2">Source</th>
                  </tr></thead>
                  <tbody>
                    {preview.previews.map((p, i) => (
                      <tr key={`bp-${i}`} className="border-t border-[color:var(--aii-border)]/50">
                        <td className="px-2 py-1.5">{p.row}</td>
                        <td className="px-2 py-1.5 truncate max-w-[8rem]">{p.customer}</td>
                        <td className="px-2 py-1.5">{p.program_name || p.program}</td>
                        <td className="px-2 py-1.5">{p.city}, {p.destination}</td>
                        <td className="px-2 py-1.5">{p.weeks}</td>
                        <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">{p.price > 0 ? `${SYM[p.currency] || p.currency}${Number(p.price).toLocaleString()}` : "—"}</td>
                        <td className="px-2 py-1.5 text-[color:var(--aii-muted)]">{p.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {(preview.errors || []).length > 0 && (
              <div className="p-3 border-t border-[color:var(--aii-border)] bg-red-50 max-h-32 overflow-y-auto">
                <div className="text-[11px] uppercase font-bold text-red-800 mb-1">Row errors</div>
                <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                  {preview.errors.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
                </ul>
              </div>
            )}
            {(preview.warnings || []).length > 0 && (
              <div className="p-3 border-t border-[color:var(--aii-border)] bg-amber-50 max-h-32 overflow-y-auto" data-testid="bulk-warnings-block">
                <div className="text-[11px] uppercase font-bold text-amber-800 mb-1">Pricing notes</div>
                <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
                  {preview.warnings.map((w, i) => <li key={`warn-${i}`}>{w}</li>)}
                </ul>
              </div>
            )}
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button data-testid="bulk-preview-cancel" onClick={() => { setPreview(null); setPendingFile(null); if (ref.current) ref.current.value = ""; }}
                className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="bulk-preview-apply" onClick={() => run(pendingFile, false)} disabled={busy || (preview.created || 0) === 0}
                className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
                Apply upload
              </button>
            </div>
          </div>
        </div>
      )}

      {errorsOpen && result?.errors?.length > 0 && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setErrorsOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="bulk-errors-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)]">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-red-700"><AlertCircle size={16}/> Row errors · {result.errors.length}</h3>
              <p className="text-xs text-[color:var(--aii-muted)]">These rows were skipped or landed unpriced. Fix them in your file and re-upload.</p>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                {result.errors.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
              </ul>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex justify-end">
              <button data-testid="bulk-errors-close" onClick={() => setErrorsOpen(false)} className="px-4 py-2 bg-brand text-white rounded-md text-sm font-semibold hover:bg-brand-dark">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
